import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partnerApi, type Partner } from '../lib/api';
import { SETTINGS_STRINGS } from '../lib/constants';
import { useToast } from './useToast';

/**
 * Hook to manage partner profile settings
 * Provides query and mutation for fetching and updating partner profile
 */
export function usePartnerProfile() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const query = useQuery<Partner>({
    queryKey: ['partner'],
    queryFn: () => partnerApi.getPartner(),
  });

  const mutation = useMutation<Partner, Error, Partial<Partner>>({
    mutationFn: data => partnerApi.updatePartnerProfile(data),
    onSuccess: () => {
      addToast({
        variant: 'success',
        title: SETTINGS_STRINGS.ORGANIZATION_PROFILE.SUCCESS_MESSAGE,
      });
      return queryClient.invalidateQueries({ queryKey: ['partner'] });
    },
    onError: () => {
      addToast({
        variant: 'destructive',
        title: SETTINGS_STRINGS.ORGANIZATION_PROFILE.ERROR_MESSAGE,
      });
    },
  });

  return {
    partner: query.data,
    isLoading: query.isLoading,
    error: query.error,
    updateProfile: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}
